import getSpotlightItemByRowKey from '../selectors/getSpotlightItemByRowKey';
import { Icon } from '@fluentui/react/lib/Icon';
import { Link } from '@fluentui/react/lib/Link';
import { observer } from 'mobx-react-lite';
import FlashlightFilled from 'owa-fluent-icons-svg/lib/icons/FlashlightFilled';
import { Lightable, lighted, LightningId } from 'owa-lightning-v2';
import loc, { formatToArray } from 'owa-localize';
import { isSingleLineListView } from 'owa-mail-layout';
import getSelectedFolder from 'owa-mail-store/lib/utils/getSelectedFolder';
import folderNameToId from 'owa-session-store/lib/utils/folderNameToId';
import { isBrowserIE } from 'owa-user-agent/lib/userAgent';
import * as React from 'react';
import {
    spotlightTeachingMoment_title,
    spotlightTeachingMoment_description,
    spotlightTeachingMoment_description_title,
    spotlightTeachingMoment_button,
} from './SpotlightIconTeachingMoment.locstring.json';

const styles = require('./SpotlightIconTeachingMoment.scss');

export interface SpotlightIconTeachingMomentProps {
    rowKey: string;
    target: HTMLElement;
}

export default observer(function SpotlightIconTeachingMoment(
    props: SpotlightIconTeachingMomentProps
) {
    const { rowKey, target } = props;

    // Lightning bits
    const when = lightup => lightup();
    const onGotItClicked = React.useCallback(() => {
        lighted(LightningId.SpotlightPropertyIcon);
    }, []);

    const renderContent = React.useCallback(() => {
        ////////////////////////////////
        /// SINGLE LINE VIEW VARIANT ///
        ////////////////////////////////
        if (isSingleLineListView()) {
            return (
                <div className={styles.contentSlv}>
                    <div className={styles.iconContainerSlv}>
                        <Icon
                            iconName={FlashlightFilled}
                            className={styles.iconSlv}
                            role="presentation"
                        />
                    </div>
                    <span className={styles.titleSlv}>{loc(spotlightTeachingMoment_title)}</span>
                    <span
                        className={styles.descriptionSlv}
                        title={loc(spotlightTeachingMoment_description_title)}>
                        {formatToArray(
                            loc(spotlightTeachingMoment_description),
                            <Icon
                                iconName={FlashlightFilled}
                                className={styles.iconInDescription}
                            />
                        )}
                    </span>
                    <Link className={styles.gotItButtonSlv} onClick={onGotItClicked}>
                        {loc(spotlightTeachingMoment_button)}
                    </Link>
                </div>
            );
        }

        /////////////////////////////////
        /// THREE COLUMN VIEW VARIANT ///
        /////////////////////////////////
        return (
            <div className={styles.contentThreeColumn}>
                <div className={styles.iconContainerThreeColumn}>
                    <Icon
                        iconName={FlashlightFilled}
                        className={styles.iconThreeColumn}
                        role="presentation"
                    />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <span className={styles.titleThreeColumn}>
                        {loc(spotlightTeachingMoment_title)}
                    </span>
                    <span
                        className={styles.descriptionThreeColumn}
                        title={loc(spotlightTeachingMoment_description_title)}>
                        {formatToArray(
                            loc(spotlightTeachingMoment_description),
                            <Icon
                                iconName={FlashlightFilled}
                                className={styles.iconInDescription}
                            />
                        )}
                    </span>
                    <Link className={styles.gotItButtonThreeColumn} onClick={onGotItClicked}>
                        {loc(spotlightTeachingMoment_button)}
                    </Link>
                </div>
            </div>
        );
    }, []);

    /**
     * It's not worth fixing this component for IE since this is just a teaching
     * moment.
     */
    if (isBrowserIE()) {
        return null;
    }

    /**
     * Don't render if we're not in the inbox.
     */
    const selectedFolder = getSelectedFolder();
    const isInboxSelected = selectedFolder?.FolderId?.Id === folderNameToId('inbox');
    if (!isInboxSelected) {
        return null;
    }

    /**
     * Don't render if no anchor element (target) is present in the DOM or
     * if the item isn't the target for the teaching moment.
     */
    if (!target || !getSpotlightItemByRowKey(rowKey)?.isTeachingMomentTarget) {
        return null;
    }

    return (
        <Lightable lid={LightningId.SpotlightPropertyIcon} when={when} disposeOnUnmount={false}>
            <div className={styles.container}>
                <div className={styles.beak}></div>
                {renderContent()}
            </div>
        </Lightable>
    );
});
